import { ErrorMessage, Field, useField } from 'formik';
import React, { Fragment, type JSX } from 'react';

interface RadioProps {
  name: string;
  value: string;
  label: string;
  validate?: (value: string) => string;
}

interface RadioGroupProps {
  name: string;
  items: {
    value: string;
    label: string;
  }[];
  validate?: (value: string) => string;
}

/**
 * Since we only want to display one validation error message
 * per radio group, and radio buttons always come in groups,
 * we created a component which, display a list of radio buttons
 * and displays one validation error.
 *
 * @example
 * <RadioGroup name="type" items={[
 *   { value: 'pupil', label: 'Leerling' },
 *   { value: 'caretaker', label: 'Ouder/verzorger' },
 * ]} validate={validateRequired}/>
 *
 */
export const RadioGroup = (props: RadioGroupProps): JSX.Element => {
  const { name, items }: RadioGroupProps = props;

  const radioButtons = items.map(item => (
    <Radio key={item.value}
      name={name}
      label={item.label}
      value={item.value}
      validate={props.validate}/>
  ));

  return (
    <Fragment>
      <div className="flex">
        {radioButtons}
      </div>
      <ErrorMessage name="type"
        component="div"
        className="validation-error pr-3"/>
    </Fragment>
  );
};

/**
 * React radio field helper. This field should only be used
 * by the RadioGroup component since we only want to display
 * one validation error per group.
 *
 * @example
 * <Radio name="pupil.gender" label="Man" value="male"/>
 * <Radio name="pupil.gender" label="Vrouw" value="female"/>
 */
const Radio = (props: RadioProps): JSX.Element => {
  const { name, label, value, validate }: RadioProps = props;
  const meta = useField(props)[1];

  let cssClasses: string = 'bem-radio__label';

  if (!!meta.touched && !!meta.error) {
    cssClasses += ' error';
  }

  if (meta.value === value) {
    cssClasses += ' checked';
  }

  return (
    <label className={cssClasses}>
      <Field type="radio"
        className="bem-radio"
        name={name}
        value={value}
        validate={validate} />
      {label}
    </label>
  );
};
